<script setup>
/**
 * PortfolioSelector - Dropdown for switching between portfolios
 *
 * Shows current portfolio and allows switching.
 * Owner role gets link to portfolio management.
 */
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { usePortfolioStore } from '@/store/usePortfolioStore'

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false
  },
  headerMode: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['portfolioChanged'])

const portfolioStore = usePortfolioStore()
const isOpen = ref(false)
const dropdownRef = ref(null)

// Computed properties from store
const portfolios = computed(() => portfolioStore.portfolios)
const activePortfolio = computed(() => portfolioStore.activePortfolio)
const isLoading = computed(() => portfolioStore.isLoading || portfolioStore.isChangingActive)

// Toggle dropdown
const toggleDropdown = () => {
  if (!isLoading.value) {
    isOpen.value = !isOpen.value
  }
}

// Select a portfolio
const selectPortfolio = async (portfolio) => {
  if (portfolio.id === portfolioStore.activePortfolioId) {
    isOpen.value = false
    return
  }

  const result = await portfolioStore.setActivePortfolio(portfolio.id)
  if (result.success) {
    emit('portfolioChanged', portfolio)
  }
  isOpen.value = false
}

// Close on click outside
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false
  }
}

// Close on escape
const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
})

// Role badge color
const getRoleBadgeClass = (role) => {
  if (role === 'owner') {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
  }
  return 'bg-(--nviya-figure-bg) text-(--nviya-text-muted)'
}
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <!-- Header mode: compact for top nav bar -->
    <button
      v-if="headerMode"
      @click="toggleDropdown"
      :disabled="isLoading"
      :class="[
        'flex items-center gap-2 rounded-full border px-3 py-1 transition-colors',
        'bg-white border-(--nviya-border-strong) hover:border-(--nviya-border-strong) text-slate-700 dark:bg-transparent dark:border-white/20 dark:hover:border-white/40 dark:text-white',
        isLoading ? 'opacity-50 cursor-wait' : ''
        ]"
    >
      <FontAwesome :icon="['fas', 'briefcase']" class="w-3.5 h-3.5 text-slate-400 dark:text-white/60" />
      <span class="text-xs font-medium max-w-[120px] truncate">
        {{ activePortfolio?.name || 'Portfolio' }}
      </span>
      <FontAwesome
        :icon="['fas', isOpen ? 'chevron-up' : 'chevron-down']"
        class="w-2.5 h-2.5 text-slate-400 dark:text-white/50"
      />
    </button>

    <!-- Collapsed state: just icon -->
    <button
      v-else-if="collapsed"
      @click="toggleDropdown"
      :disabled="isLoading"
      :class="[
        'w-full flex items-center justify-center p-2 rounded-lg transition-colors',
        'hover:bg-(--nviya-figure-bg) text-slate-600 dark:hover:bg-slate-700 dark:text-slate-300',
        isLoading ? 'opacity-50 cursor-wait' : ''
        ]"
      :title="activePortfolio?.name || 'Select Portfolio'"
    >
      <FontAwesome :icon="['fas', 'briefcase']" class="w-4 h-4" />
    </button>

    <!-- Expanded state: full selector -->
    <button
      v-else
      @click="toggleDropdown"
      :disabled="isLoading"
      :class="[
        'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-left',
        'hover:bg-(--nviya-figure-bg) dark:hover:bg-slate-700',
        isLoading ? 'opacity-50 cursor-wait' : ''
        ]"
    >
      <FontAwesome
        :icon="['fas', 'briefcase']"
        :class="['w-4 h-4 flex-shrink-0', 'text-slate-500 dark:text-slate-400']"
      />
      <div class="flex-1 min-w-0">
        <p :class="['text-sm font-medium truncate', 'text-slate-900 dark:text-white']">
          {{ activePortfolio?.name || 'Select Portfolio' }}
        </p>
        <p :class="['text-xs truncate', 'text-slate-500 dark:text-slate-400']">
          {{ activePortfolio ? `${activePortfolio.companyCount} companies` : 'No portfolio selected' }}
        </p>
      </div>
      <FontAwesome
        :icon="['fas', isOpen ? 'chevron-up' : 'chevron-down']"
        :class="['w-3 h-3 flex-shrink-0', 'text-slate-400 dark:text-slate-400']"
      />
    </button>

    <!-- Dropdown menu -->
    <Transition name="dropdown">
      <div
        v-if="isOpen"
        :class="[
          'absolute mt-1 rounded-lg shadow-(--nviya-shadow-lg) border z-50 max-h-64 overflow-y-auto',
          headerMode ? 'right-0 w-64' : 'left-0 right-0',
          'n-raised border'
          ]"
      >
        <!-- Portfolio list -->
        <div class="py-1">
          <button
            v-for="portfolio in portfolios"
            :key="portfolio.id"
            @click="selectPortfolio(portfolio)"
            :class="[
              'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
              portfolio.id === activePortfolio?.id
              ? 'bg-(--nviya-selected-bg)'
              : 'hover:bg-(--nviya-figure-bg) dark:hover:bg-slate-700'
              ]"
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span :class="['text-sm font-medium truncate', 'text-slate-900 dark:text-white']">
                  {{ portfolio.name }}
                </span>
                <span
                  v-if="portfolio.isDefault"
                  :class="['text-[10px] px-1.5 py-0.5 rounded', 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400']"
                >
                  Default
                </span>
                <span
                  v-if="portfolio.role"
                  :class="['text-[10px] px-1.5 py-0.5 rounded capitalize', getRoleBadgeClass(portfolio.role)]"
                >
                  {{ portfolio.role }}
                </span>
              </div>
              <p :class="['text-xs truncate', 'text-slate-500 dark:text-slate-400']">
                {{ portfolio.companyCount }} companies
              </p>
            </div>
            <FontAwesome
              v-if="portfolio.id === activePortfolio?.id"
              :icon="['fas', 'check']"
              :class="['w-3 h-3', 'text-surface dark:text-blue-400']"
            />
          </button>
        </div>

        <!-- Manage link (for owners/admins) -->
        <div
          v-if="portfolioStore.ownedPortfolios.length > 0 || portfolioStore.isAdmin"
          :class="['border-t', 'border-(--nviya-border)']"
        >
          <NuxtLink
            to="/admin/portfolios"
            @click="isOpen = false"
            :class="[
              'flex items-center gap-2 px-3 py-2 text-sm transition-colors',
              'text-primary hover:bg-(--nviya-figure-bg) dark:text-blue-400 dark:hover:bg-slate-700'
              ]"
          >
            <FontAwesome :icon="['fas', 'cog']" class="w-3 h-3" />
            Manage Portfolios
          </NuxtLink>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
